import { useEffect, useState, useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { Crown } from 'lucide-react';
import { getDeviceCapability, DeviceCapabilityInfo } from '../utils/deviceCapability';
import { RecordingQuality } from '../store/canvasStore';

export interface QualityTierConfig {
  enabledQualities: RecordingQuality[];
  defaultQuality: RecordingQuality;
  showHint: boolean;
}

export interface UseDeviceCapabilityQualityResult {
  deviceTier: DeviceCapabilityInfo['tier'] | null;
  qualityConfig: QualityTierConfig | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to manage recording quality based on device capability
 * Returns tier-specific quality configuration
 * 
 * Device Tiers:
 * - Low: 720p only (prevents poor performance)
 * - Medium: 720p + 1080p (defaults to 720p)
 * - High: All resolutions (defaults to 1080p)
 * 
 * @example
 * ```tsx
 * const { deviceTier, qualityConfig } = useDeviceCapabilityQuality();
 * 
 * if (qualityConfig) {
 *   // Only show enabled qualities
 *   const items = qualityConfig.enabledQualities.map(quality => ({
 *     key: quality,
 *     label: quality
 *   }));
 * }
 * ```
 */
export function useDeviceCapabilityQuality(): UseDeviceCapabilityQualityResult {
  const [deviceTier, setDeviceTier] = useState<DeviceCapabilityInfo['tier'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const detectDeviceCapability = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const capability = await getDeviceCapability();
        
        if (isMounted) {
          setDeviceTier(capability.tier);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to detect device capability');
          // Default to medium tier on error to be safe
          setDeviceTier('medium');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    detectDeviceCapability();

    return () => {
      isMounted = false;
    };
  }, []);

  const qualityConfig = useMemo((): QualityTierConfig | null => {
    if (!deviceTier) return null;

    switch (deviceTier) {
      case 'high':
        return {
          enabledQualities: ['720p', '1080p', '2k'],
          defaultQuality: '1080p',
          showHint: false,
        };
      case 'medium':
        return {
          enabledQualities: ['720p', '1080p'],
          defaultQuality: '720p',
          showHint: true,
        };
      case 'low':
        return {
          enabledQualities: ['720p'],
          defaultQuality: '720p',
          showHint: true,
        };
      default:
        return {
          enabledQualities: ['720p', '1080p'],
          defaultQuality: '720p',
          showHint: true,
        };
    }
  }, [deviceTier]);

  return {
    deviceTier,
    qualityConfig,
    isLoading,
    error,
  };
}

/**
 * Helper function to get quality dropdown items based on device tier
 * Combines device capability limits with user permission limits
 * 
 * @param qualityConfig Device-based quality config
 * @param isResolutionLimited Whether user has premium resolution access
 * @param currentQuality Current selected quality
 * @returns Dropdown menu items with disabled states
 */
export function getQualityDropdownItems(
  qualityConfig: QualityTierConfig | null,
  isResolutionLimited: boolean,
  currentQuality: RecordingQuality
) {
  if (!qualityConfig) return [];

  const allQualityItems = [
    {
      key: "720p" as const,
      label: (
        <div>
          <Typography.Text strong>720p (HD)</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Quick recording to share on the go.
          </Typography.Text>
        </div>
      ),
    },
    {
      key: "1080p" as const,
      label: (
        <Flex vertical>
          <Flex align="center" gap={8}>
            <Typography.Text strong style={{ margin: 0 }}>
              1080p (Full HD)
            </Typography.Text>
            {isResolutionLimited && (
              <Crown size={16} fill="#ffb229" color="#ffb229" />
            )}
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: 12, margin: 0 }}>
            Sharp recordings that look great on most screens.
          </Typography.Text>
        </Flex>
      ),
    },
    {
      key: "2k" as const,
      label: (
        <Flex vertical>
          <Flex align="center" gap={8}>
            <Typography.Text strong>2k (Quad HD)</Typography.Text>
            {isResolutionLimited && (
              <Crown size={16} fill="#ffb229" color="#ffb229" />
            )}
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: 12, margin: 0 }}>
            Enhanced detail for larger monitors.
          </Typography.Text>
        </Flex>
      ),
    },
  ];

  return allQualityItems.map(item => ({
    ...item,
    // Only disable based on device capability, NOT user tier
    // Premium options should remain clickable to trigger upgrade modal
    disabled: !qualityConfig.enabledQualities.includes(item.key),
  }));
}
